import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Minibar from "../components/Minibar";
import PageHeader from "../components/PageHeader";
import { useContext, useEffect, useState } from "react";
import { UserRole } from "../models/User";
import { PageContext } from "../contexts/PageContext";
import { Asset } from "../models/Asset";
import WsManagement, { getWsInstance } from "../api/WsManagement";

export default function AssetsPage() {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });
  const [filteredRows, setFilteredRows] = useState<Asset[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const { isLoggedIn, user, org } = useContext(PageContext);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [wsManagement] = useState<WsManagement>(getWsInstance());

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/maintenance/404");
      return;
    }
    if (!user || !user.role) {
      return;
    }
    fetchAssets(user.role);
  }, [isLoggedIn, user]);

  useEffect(() => {
    const filtered = assets.filter(
      (asset) =>
        asset.name.toLowerCase().includes(filter.toLowerCase()) ||
        asset.category.toLowerCase().includes(filter.toLowerCase())
    );
    setFilteredRows(filtered);
  }, [filter, assets]);

  const fetchAssets = async (role: UserRole) => {
    try {
      setAssets(await wsManagement.uiService.loadAssets());
    } catch (err) {
      console.error("Failed to fetch assets:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedAssets = [...assets].sort((a, b) => {
      const aValue = (a as any)[key] ?? "";
      const bValue = (b as any)[key] ?? "";

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredRows(sortedAssets);
  };

  return (
    <Box sx={{ display: "flex", overflowX: "hidden" }}>
      <CssBaseline />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          m: 0,
          backgroundColor: "#212121",
          color: "#ECECEC",
          minHeight: "100vh",
        }}
      >
        <Backdrop
          open={loading}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <PageHeader
          title={"Assets"}
          rightComponent={[
            <Minibar
              minibarItems={[
                {
                  text: "Edit/Create Asset",
                  icon: <Edit />,
                  nav: "/edit-asset/new",
                },
              ]}
              barLength={1}
              searchLabel="Search"
              onSearch={(e) => setFilter(e)}
            />,
          ]}
        />
        {/* {user?.role === UserRole.SystemAdmin && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Organization</InputLabel>
            <Select
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
            >
              <MenuItem value="">All Organizations</MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org._id} value={org._id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )} */}
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left" width="30%">
                  <TableSortLabel
                    active={sortConfig.key === "name"}
                    direction={
                      sortConfig.key === "name" ? sortConfig.direction : "asc"
                    }
                    onClick={() => handleSort("name")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left" width="10%">
                  Category
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((asset) => (
                <TableRow hover key={asset._id}>
                  <TableCell align="left">
                    <Tooltip title="Edit Asset" arrow>
                      <Link
                        onClick={() => navigate(`/edit-asset/${asset._id}`)}
                        style={{ color: "#b0d1ff", cursor: "pointer" }}
                      >
                        {asset.name}
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="left">
                    {asset.category || "Not categorized"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
